"use client";

import { type FC, type Ref, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { HouseSearchCard, type HouseSearchCardProps } from "@huub-web/shared";
import { mergeEvents } from "@huub-web/utils";
import { type UIAction, useActions } from "@huub-web/ui";

import { useTemplates } from "../../hooks/useTemplates";
import { usePageContent } from "../../hooks/usePageContent";

export type HouseSearchCardTemplateProps = {
  placeholder?: string;
  searchEndpoint: string;
  action?: UIAction;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * HouseSearchCardTemplate component for house search functionality
 */
export const HouseSearchCardTemplate: FC<HouseSearchCardTemplateProps> = ({
  searchEndpoint,
  action,
  ref,
  ...props
}) => {
  const { $get } = useTemplates();
  const { handleAction } = useActions();
  const { t } = useTranslation();

  const contentItem = useMemo(
    () => ({
      type: "search" as const,
      label:
        props.placeholder ??
        t("houseSearchCard.searchPlaceholder", "Zoek een woning"),
    }),
    [props.placeholder, t],
  );
  usePageContent("housesearch", contentItem);

  const handlePropertySearch = () => {
    //
  };

  const handleSearchStart = () => {
    //
  };

  const handlePropertySelect: HouseSearchCardProps["onPropertySelect"] = (
    address,
  ) => {
    if (action) {
      if (action.type === "navigate") {
        const context = (action.payload?.context ?? []).map((c) =>
          c.replace("[id]", address?.address_object_id ?? "[id]"),
        );
        const parsedAction: UIAction = {
          ...action,
          payload: {
            ...action.payload,
            context,
          },
        };
        handleAction(parsedAction);
      } else {
        handleAction(action);
      }
    }
  };

  const getSearchResults: HouseSearchCardProps["getSearchResults"] = async (
    query,
  ) => {
    if (!searchEndpoint) {
      console.warn("[MaximumMortgageTemplate] No search endpoint provided");
      return [];
    }

    const url = `${searchEndpoint}?query=${encodeURIComponent(query)}`;

    return $get(url).then((data) => data?.data?.results ?? []);
  };

  return (
    <HouseSearchCard
      ref={ref}
      onSearch={mergeEvents(
        handlePropertySearch,
        (props as HouseSearchCardProps).onSearch,
      )}
      onSearchStart={mergeEvents(
        handleSearchStart,
        (props as HouseSearchCardProps).onSearchStart,
      )}
      onPropertySelect={mergeEvents(
        handlePropertySelect,
        (props as HouseSearchCardProps).onPropertySelect,
      )}
      clearOnSelect={false}
      getSearchResults={getSearchResults}
      {...props}
    />
  );
};
